import React, {
  useRef,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  CircularProgress,
  Button,
  IconButton,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import UndoIcon from '@mui/icons-material/Undo';
import GestureIcon from '@mui/icons-material/Gesture';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import {
  Marker,
  Source,
  Layer,
  type MapRef,
  type MarkerDragEvent,
} from '@vis.gl/react-maplibre';
import type { MapMouseEvent } from 'maplibre-gl';
import MapView from 'components/Map/MapView';
import { useMapFlyTo } from 'hooks/useMapFlyTo';
import { CustomMuiThemeProvider } from 'theme/CustomMuiThemeProvider';
import useCustomerInfo from 'hooks/useCustomerInfo';
import { hasNoSatelliteCoverage } from 'utils/siteRoadOverlay';
import {
  geoPointsToZoneGeog,
  zoneGeogToGeoPoints,
  type GeoJSONPolygon,
  type GeoPoint,
} from 'utils/geohash';
import { useAppSelector } from 'store/hook';
import { selectLatestTagData } from 'store/device.slice';
import { selectAssets } from 'store/asset.slice';
import { images } from 'components/Asset/PNG';
import { mapVehiclesAssets } from 'utils/helpers/mapAssetImage';

// Esri's satellite/neutral-road raster sources cap out at zoom 19 (see
// utils/maplibre.ts) — capping the map's own maxZoom at the same level stops
// users zooming in past the point where tiles just blur instead of
// sharpening, replacing the old Cesium `screenSpaceCameraController
// .minimumZoomDistance` floor (Cesium's imagery placeholder tiles are valid
// 200 responses with "no data" baked into the pixels, so there was never a
// reliable way to detect them directly either way).
const MAX_ZOOM = 19;
const MAP_HEIGHT = 460;

// The old Cesium drag handling hit-tested every vertex within this pixel
// radius of the cursor to find the "nearest" one to grab. MapLibre `Marker`s
// are real DOM elements, so dragging now targets the exact marker under the
// pointer instead of a nearest-neighbour search — this constant now only
// pads each vertex's invisible hit area (in drag mode) so it stays an easy,
// forgiving grab target rather than shrinking to the visible dot's size.
const DRAG_HIT_PADDING_PX = 20;

// Zoom levels replacing the old flyTo altitudes (metres) — there is no direct
// altitude→zoom formula, these were picked to match the old visual closeness.
const RING_FOCUS_ZOOM = 17;
const CENTER_FOCUS_ZOOM = 17;
const SITE_FOCUS_ZOOM = 16;

const LOCATION_TYPE_COLORS: Record<string, string> = {
  loading: '#4ade80',
  dumping: '#fbbf24',
  parking: '#a78bfa',
  workshop: '#94a3b8',
  fueling: '#fb923c',
  weighbridge: '#2dd4bf',
  crushing: '#f87171',
  crusher: '#f87171',
  processing: '#22d3ee',
  stockpile: '#e879f9',
  intersection: '#60a5fa',
};

interface TruckDot {
  deviceId: string;
  lat: number;
  lon: number;
  bumper: string;
  ignitionOn: boolean;
  assetType: string;
}

export interface OtherLocation {
  id: string;
  name: string;
  locationType: string;
  centroidLat?: number;
  centroidLon?: number;
  geohashes?: string[];
  zoneGeog?: GeoJSONPolygon | null;
}

function buildTruckLabel(bumperNumber: string, ignitionOn: boolean): string {
  const canvas = document.createElement('canvas');
  canvas.width = 150;
  canvas.height = 34;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const dotColor = ignitionOn ? '#4ade80' : '#f87171';
  ctx.fillStyle = 'rgba(10,15,30,0.88)';
  ctx.strokeStyle = ignitionOn ? '#4ade80' : '#94a3b8';
  ctx.lineWidth = 1.5;
  const r = 5;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(canvas.width - r, 0);
  ctx.quadraticCurveTo(canvas.width, 0, canvas.width, r);
  ctx.lineTo(canvas.width, canvas.height - r);
  ctx.quadraticCurveTo(
    canvas.width,
    canvas.height,
    canvas.width - r,
    canvas.height
  );
  ctx.lineTo(r, canvas.height);
  ctx.quadraticCurveTo(0, canvas.height, 0, canvas.height - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.fillStyle = dotColor;
  ctx.beginPath();
  ctx.arc(11, canvas.height / 2, 4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 14px sans-serif';
  ctx.textAlign = 'left';
  ctx.textBaseline = 'middle';
  ctx.fillText(bumperNumber, 20, canvas.height / 2);
  return canvas.toDataURL();
}

function getAssetPng(assetType: string): string {
  const svgKey = mapVehiclesAssets(assetType);
  const pngKey =
    svgKey === 'haulTruckImage' ||
    svgKey === 'lightTruckImage' ||
    svgKey === 'heavyTruckImage'
      ? 'fallbackhaul_truckImageSmall'
      : 'fallbackloaderImageSmall';
  return images[pngKey] ?? '';
}

function buildPinIcon(colorHex: string): string {
  const w = 32;
  const h = 44;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  if (!ctx) return '';
  const cx = w / 2;
  const cy = 14;
  const r = 12;
  ctx.fillStyle = colorHex;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.arc(cx, cy, r, Math.PI, 0, false);
  ctx.lineTo(cx + r * 0.6, cy + r * 0.9);
  ctx.quadraticCurveTo(cx, h - 2, cx - r * 0.6, cy + r * 0.9);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.arc(cx, cy, r * 0.35, 0, Math.PI * 2);
  ctx.fillStyle = '#ffffff';
  ctx.fill();
  return canvas.toDataURL();
}

export interface LocationBoundaryResult {
  zoneGeog: GeoJSONPolygon | null;
}

interface LocationBoundaryDrawerProps {
  open: boolean;
  existingZoneGeog?: GeoJSONPolygon | null;
  /** Center the map here on open and mark it (e.g. the pin already picked). */
  initialCenter?: { lat: number; lon: number };
  /** Other saved locations, rendered for context while drawing. */
  otherLocations?: OtherLocation[];
  onSave: (result: LocationBoundaryResult) => void;
  onBack: () => void;
}

const centroidOf = (points: GeoPoint[]): GeoPoint => {
  const lat = points.reduce((s, p) => s + p.lat, 0) / points.length;
  const lon = points.reduce((s, p) => s + p.lon, 0) / points.length;
  return { lat, lon };
};

const LocationBoundaryDrawerInner = ({
  open,
  existingZoneGeog,
  initialCenter,
  otherLocations,
  onSave,
  onBack,
}: LocationBoundaryDrawerProps): JSX.Element => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { customerCode } = useCustomerInfo();
  const mapRef = useRef<MapRef | null>(null);
  const hasCenteredRef = useRef(false);
  const draftVerticesRef = useRef<GeoPoint[]>([]);
  const dragModeRef = useRef(false);
  const { flyTo } = useMapFlyTo(mapRef);

  const [mapReady, setMapReady] = useState(false);
  const [draftVertices, setDraftVertices] = useState<GeoPoint[]>([]);
  const [dragMode, setDragMode] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const latestTagData = useAppSelector(selectLatestTagData);
  const assets = useAppSelector(selectAssets);

  // device → { bumperNumber, assetType }
  const assetInfoMap = useMemo<
    Record<string, { bumper: string; assetType: string }>
  >(() => {
    const map: Record<string, { bumper: string; assetType: string }> = {};
    assets.forEach((a) => {
      if (a.device?.deviceId) {
        map[a.device.deviceId] = {
          bumper: a.bumperNumber ?? a.device.deviceId,
          assetType: a.assetType ?? 'haul_truck',
        };
      }
    });
    return map;
  }, [assets]);

  const truckDots = useMemo<TruckDot[]>(() => {
    return Object.entries(latestTagData)
      .map(([deviceId, s]) => {
        const lat = Number(s.gps_latitude);
        const lon = Number(s.gps_longitude);
        if (!isFinite(lat) || !isFinite(lon) || lat === 0 || lon === 0)
          return null;
        if (lat < -90 || lat > 90 || lon < -180 || lon > 180) return null;
        const info = assetInfoMap[deviceId];
        return {
          deviceId,
          lat,
          lon,
          bumper: info?.bumper ?? deviceId,
          ignitionOn: String(s.sb_ignition_status) === 'true',
          assetType: info?.assetType ?? 'haul_truck',
        };
      })
      .filter((d): d is TruckDot => d !== null);
  }, [latestTagData, assetInfoMap]);

  const mineSiteCenter = useMemo<GeoPoint | null>(() => {
    if (truckDots.length === 0) return null;
    const avgLat = truckDots.reduce((s, d) => s + d.lat, 0) / truckDots.length;
    const avgLon = truckDots.reduce((s, d) => s + d.lon, 0) / truckDots.length;
    return { lat: avgLat, lon: avgLon };
  }, [truckDots]);

  const truckMarkers = useMemo(() => {
    return truckDots.map((dot) => {
      const labelImage = buildTruckLabel(dot.bumper, dot.ignitionOn);
      const pngImage = getAssetPng(dot.assetType);
      return (
        <Marker
          key={dot.deviceId}
          longitude={dot.lon}
          latitude={dot.lat}
          anchor="center"
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              pointerEvents: 'none',
            }}
          >
            <Box
              component="img"
              src={labelImage}
              sx={{ height: 17, mb: '2px' }}
            />
            <Box component="img" src={pngImage} sx={{ height: 28 }} />
          </Box>
        </Marker>
      );
    });
  }, [truckDots]);

  // Other saved locations, rendered for context: one shared GeoJSON source
  // carrying every zone polygon (colored per-feature via data-driven paint),
  // plus a small label Marker at each zone's centroid/dot.
  const otherLocationZonesGeoJson = useMemo(() => {
    const fallbackHex = theme.palette.primary.main;
    const features = (otherLocations ?? [])
      .map((loc) => {
        const hull = zoneGeogToGeoPoints(loc.zoneGeog);
        if (hull.length < 3) return null;
        const colorHex =
          LOCATION_TYPE_COLORS[loc.locationType?.toLowerCase()] ?? fallbackHex;
        const ring = hull.map((p) => [p.lon, p.lat]);
        ring.push(ring[0]);
        return {
          type: 'Feature' as const,
          properties: { fillColor: colorHex, outlineColor: colorHex },
          geometry: { type: 'Polygon' as const, coordinates: [ring] },
        };
      })
      .filter((f): f is NonNullable<typeof f> => f !== null);
    return { type: 'FeatureCollection' as const, features };
  }, [otherLocations, theme.palette.primary.main]);

  const otherLocationLabels = useMemo(() => {
    const fallbackHex = theme.palette.primary.main;
    return (otherLocations ?? [])
      .map((loc) => {
        const colorHex =
          LOCATION_TYPE_COLORS[loc.locationType?.toLowerCase()] ?? fallbackHex;
        const hull = zoneGeogToGeoPoints(loc.zoneGeog);
        let labelLat = loc.centroidLat;
        let labelLon = loc.centroidLon;
        if (hull.length >= 3) {
          labelLat = hull.reduce((s, p) => s + p.lat, 0) / hull.length;
          labelLon = hull.reduce((s, p) => s + p.lon, 0) / hull.length;
        }
        if (labelLat == null || labelLon == null) return null;
        return (
          <Marker
            key={loc.id}
            longitude={labelLon}
            latitude={labelLat}
            anchor="bottom"
          >
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                pointerEvents: 'none',
              }}
            >
              {hull.length < 3 && (
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: colorHex,
                    border: '1px solid #fff',
                    mb: '2px',
                  }}
                />
              )}
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: '#fff',
                  textShadow: '0 1px 3px rgba(0,0,0,0.9)',
                  whiteSpace: 'nowrap',
                }}
              >
                {loc.name}
              </Typography>
            </Box>
          </Marker>
        );
      })
      .filter((el): el is NonNullable<typeof el> => el !== null);
  }, [otherLocations, theme.palette.primary.main]);

  // The drawn zoneGeog polygon, preloaded as the starting draft so editing
  // moves the real shape instead of drawing a brand new one from scratch.
  const initialRing = useMemo(
    () => zoneGeogToGeoPoints(existingZoneGeog),
    [existingZoneGeog]
  );

  useEffect(() => {
    if (open) {
      setDraftVertices(initialRing);
      setDragMode(false);
      setIsDragging(false);
      hasCenteredRef.current = false;
      // The dialog's content (and the map inside it) is unmounted while
      // closed, so a fresh MapView instance mounts on every open — reset
      // mapReady so the effects gated on it (handler attach, camera center)
      // re-run against that new instance instead of staying stuck on "already
      // ready" from a previous session.
      setMapReady(false);
    }
  }, [open, initialRing]);

  useEffect(() => {
    draftVerticesRef.current = draftVertices;
  }, [draftVertices]);

  useEffect(() => {
    dragModeRef.current = dragMode;
  }, [dragMode]);

  // Draw mode: left-click adds a vertex, right-click removes the last one —
  // replaces the Cesium LEFT_CLICK/RIGHT_CLICK handlers (pickEllipsoid +
  // Cartographic conversion) with MapLibre's native events, which already
  // hand back lon/lat directly.
  useEffect(() => {
    if (!mapReady) return;
    const map = mapRef.current?.getMap();
    if (!map) return;

    const handleClick = (e: MapMouseEvent) => {
      if (dragModeRef.current) return;
      const { lat, lng } = e.lngLat;
      setDraftVertices((prev) => [...prev, { lat, lon: lng }]);
    };
    const handleContextMenu = (e: MapMouseEvent) => {
      e.preventDefault();
      setDraftVertices((prev) => prev.slice(0, -1));
    };

    map.on('click', handleClick);
    map.on('contextmenu', handleContextMenu);
    return () => {
      map.off('click', handleClick);
      map.off('contextmenu', handleContextMenu);
    };
  }, [mapReady]);

  useEffect(() => {
    if (!mapReady || hasCenteredRef.current) return;
    if (initialRing.length) {
      hasCenteredRef.current = true;
      const center = centroidOf(initialRing);
      flyTo(center.lat, center.lon, RING_FOCUS_ZOOM);
      return;
    }
    if (initialCenter) {
      hasCenteredRef.current = true;
      flyTo(initialCenter.lat, initialCenter.lon, CENTER_FOCUS_ZOOM);
      return;
    }
    if (mineSiteCenter) {
      hasCenteredRef.current = true;
      flyTo(mineSiteCenter.lat, mineSiteCenter.lon, SITE_FOCUS_ZOOM);
    }
  }, [mapReady, initialRing, initialCenter, mineSiteCenter, flyTo]);

  const handleUndo = () => {
    setDraftVertices((prev) => prev.slice(0, -1));
  };

  const handleSave = () => {
    if (draftVertices.length < 3) return;
    onSave({
      zoneGeog: geoPointsToZoneGeog(draftVertices),
    });
  };

  // Releases a mid-drag vertex grab — needed when the dialog is closed
  // without a drag-end (e.g. clicking the X mid-drag). The map instance
  // itself is torn down by the Dialog unmounting, so there is no camera
  // controller state to restore (unlike Cesium's screenSpaceCameraController).
  const handleBack = () => {
    setIsDragging(false);
    onBack();
  };

  const handleVertexDragStart = useCallback(() => {
    setIsDragging(true);
  }, []);

  const handleVertexDrag = useCallback((idx: number, e: MarkerDragEvent) => {
    const { lat, lng } = e.lngLat;
    setDraftVertices((prev) => {
      if (idx >= prev.length) return prev;
      const updated = [...prev];
      updated[idx] = { lat, lon: lng };
      return updated;
    });
  }, []);

  const handleVertexDragEnd = useCallback(() => {
    setIsDragging(false);
  }, []);

  const primaryColorHex = theme.palette.primary.main;
  const pinIcon = useMemo(() => buildPinIcon('#f87171'), []);

  // Vertex markers: purely visual (pointer-events disabled) in draw mode so
  // clicks pass through to the map underneath and add a new vertex, exactly
  // like the old scene-pick did regardless of what was rendered at that
  // pixel; draggable with a generous invisible hit-padding in drag mode.
  const vertexMarkers = useMemo(() => {
    return draftVertices.map((p, i) => {
      const hitSize = 10 + DRAG_HIT_PADDING_PX;
      return (
        <Marker
          key={`vertex-${i}`}
          longitude={p.lon}
          latitude={p.lat}
          anchor="center"
          draggable={dragMode}
          style={{
            pointerEvents: dragMode ? 'auto' : 'none',
            cursor: dragMode ? (isDragging ? 'grabbing' : 'grab') : 'default',
          }}
          onDragStart={handleVertexDragStart}
          onDrag={(e) => {
            handleVertexDrag(i, e);
          }}
          onDragEnd={handleVertexDragEnd}
        >
          <Box
            sx={{
              width: hitSize,
              height: hitSize,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: '#fff',
                border: `1.5px solid ${primaryColorHex}`,
              }}
            />
          </Box>
        </Marker>
      );
    });
  }, [
    draftVertices,
    dragMode,
    isDragging,
    primaryColorHex,
    handleVertexDragStart,
    handleVertexDrag,
    handleVertexDragEnd,
  ]);

  // Draft polygon-in-progress: a closed-ring outline (all points + back to
  // the first) plus a fill once there are enough points — replaces the
  // Cesium polygon/polyline Entities.
  const draftGeoJson = useMemo(() => {
    const features: Array<{
      type: 'Feature';
      properties: { kind: string };
      geometry: { type: 'Polygon' | 'LineString'; coordinates: any };
    }> = [];

    if (draftVertices.length >= 3) {
      const ring = draftVertices.map((p) => [p.lon, p.lat]);
      ring.push(ring[0]);
      features.push({
        type: 'Feature',
        properties: { kind: 'fill' },
        geometry: { type: 'Polygon', coordinates: [ring] },
      });
    }
    if (draftVertices.length >= 2) {
      const line = draftVertices.map((p) => [p.lon, p.lat]);
      line.push(line[0]);
      features.push({
        type: 'Feature',
        properties: { kind: 'line' },
        geometry: { type: 'LineString', coordinates: line },
      });
    }
    return { type: 'FeatureCollection' as const, features };
  }, [draftVertices]);

  return (
    <Dialog
      open={open}
      onClose={handleBack}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: (th) => alpha(th.palette.primary.main, 0.15),
          backgroundImage: 'none',
        },
      }}
    >
      {/* ── Header ── */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          px: 3,
          pt: 2.5,
          pb: 2,
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Box
            sx={{
              width: 36,
              height: 36,
              borderRadius: '10px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: (th) => alpha(th.palette.primary.main, 0.12),
              border: '1px solid',
              borderColor: (th) => alpha(th.palette.primary.main, 0.25),
            }}
          >
            <GestureIcon sx={{ fontSize: 18, color: 'primary.main' }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={700} lineHeight={1.2}>
              {t('loc_importer_boundary_step_title')}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {dragMode
                ? t('boundary_map_drag_hint')
                : t('loc_importer_boundary_hint')}
            </Typography>
          </Box>
        </Box>
        <ToggleButtonGroup
          value={dragMode ? 'drag' : 'draw'}
          exclusive
          size="small"
          onChange={(_, val) => {
            if (val !== null) setDragMode(val === 'drag');
          }}
          sx={{ height: 32, mr: 1 }}
        >
          <ToggleButton value="draw" sx={{ fontWeight: 600, px: 1.2 }}>
            <EditOutlinedIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {t('boundary_map_mode_draw')}
          </ToggleButton>
          <ToggleButton value="drag" sx={{ fontWeight: 600, px: 1.2 }}>
            <OpenWithIcon sx={{ fontSize: 16, mr: 0.5 }} />
            {t('boundary_map_mode_drag')}
          </ToggleButton>
        </ToggleButtonGroup>
        <IconButton onClick={handleBack} size="small">
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 1, pb: 2, overflowY: 'hidden' }}>
        <Box
          onWheel={(e) => {
            e.stopPropagation();
          }}
          sx={{
            height: MAP_HEIGHT,
            position: 'relative',
            borderRadius: 2,
            overflow: 'hidden',
            border: '1px solid',
            borderColor: (th) => alpha(th.palette.divider, 0.5),
            cursor: dragMode ? (isDragging ? 'grabbing' : 'grab') : 'crosshair',
          }}
        >
          {!mapReady && (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'background.default',
                zIndex: 5,
              }}
            >
              <CircularProgress size={28} />
            </Box>
          )}

          <MapView
            ref={mapRef}
            basemap={
              hasNoSatelliteCoverage(customerCode) ? undefined : 'satellite'
            }
            style={{ width: '100%', height: '100%' }}
            maxZoom={MAX_ZOOM}
            onLoad={() => {
              setMapReady(true);
            }}
          >
            {otherLocationZonesGeoJson.features.length > 0 && (
              <Source
                id="location-boundary-drawer-other-zones"
                type="geojson"
                data={otherLocationZonesGeoJson}
              >
                <Layer
                  id="location-boundary-drawer-other-zones-fill"
                  type="fill"
                  paint={{
                    'fill-color': ['get', 'fillColor'],
                    'fill-opacity': 0.25,
                  }}
                />
                <Layer
                  id="location-boundary-drawer-other-zones-line"
                  type="line"
                  paint={{
                    'line-color': ['get', 'outlineColor'],
                    'line-width': 2,
                    'line-opacity': 0.8,
                  }}
                />
              </Source>
            )}
            {otherLocationLabels}
            {truckMarkers}

            {initialCenter && (
              <Marker
                longitude={initialCenter.lon}
                latitude={initialCenter.lat}
                anchor="bottom"
              >
                <Box
                  component="img"
                  src={pinIcon}
                  sx={{ height: 44, pointerEvents: 'none' }}
                />
              </Marker>
            )}

            {draftGeoJson.features.length > 0 && (
              <Source
                id="location-boundary-draft"
                type="geojson"
                data={draftGeoJson}
              >
                <Layer
                  id="location-boundary-draft-fill"
                  type="fill"
                  filter={['==', ['get', 'kind'], 'fill']}
                  paint={{ 'fill-color': primaryColorHex, 'fill-opacity': 0.2 }}
                />
                <Layer
                  id="location-boundary-draft-line"
                  type="line"
                  filter={['==', ['get', 'kind'], 'line']}
                  paint={{ 'line-color': primaryColorHex, 'line-width': 2 }}
                />
              </Source>
            )}

            {vertexMarkers}
          </MapView>

          {/* Points / cells badge */}
          <Box
            sx={{
              position: 'absolute',
              bottom: 10,
              left: '50%',
              transform: 'translateX(-50%)',
              display: 'flex',
              alignItems: 'center',
              gap: 0.75,
              zIndex: 4,
              pointerEvents: 'none',
            }}
          >
            <Chip
              size="small"
              label={t('loc_importer_boundary_points', {
                count: draftVertices.length,
              })}
              sx={{
                bgcolor: (th) => alpha(th.palette.background.paper, 0.9),
                backdropFilter: 'blur(4px)',
              }}
            />
          </Box>
        </Box>

        {draftVertices.length < 3 && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mt: 1 }}
          >
            {t('loc_importer_boundary_min_points')}
          </Typography>
        )}
      </DialogContent>

      {/* ── Actions ── */}
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 1,
          borderTop: '1px solid',
          borderColor: (th) => alpha(th.palette.divider, 0.5),
        }}
      >
        <Button
          size="small"
          startIcon={<UndoIcon fontSize="small" />}
          onClick={handleUndo}
          disabled={draftVertices.length === 0}
          sx={{ borderRadius: '8px', fontWeight: 600, mr: 'auto' }}
        >
          {t('loc_importer_boundary_undo')}
        </Button>
        <Button
          variant="outlined"
          color="inherit"
          onClick={handleBack}
          sx={{ borderRadius: '8px', fontWeight: 600 }}
        >
          {t('loc_importer_boundary_back')}
        </Button>
        <Button
          variant="contained"
          onClick={handleSave}
          disabled={draftVertices.length < 3}
          sx={{ borderRadius: '8px', fontWeight: 600, px: 3 }}
        >
          {t('loc_importer_boundary_save')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const Memoized = React.memo(LocationBoundaryDrawerInner);

export const LocationBoundaryDrawer = (
  props: LocationBoundaryDrawerProps
): JSX.Element => (
  <CustomMuiThemeProvider>
    <Memoized {...props} />
  </CustomMuiThemeProvider>
);
